<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';

import { CopyButton } from '~/components/CopyButton';

const { copy } = useClipboard();

const { trackCopiedCommercialId } = useEvents();

const props = withDefaults(
	defineProps<{
		ad?: Property['ad'];
		isLoading?: boolean;
		summary?: string;
		commercialId?: string;
		numberOfSkeletonGroups?: number;
		numberOfSkeletonLinesPerGroup?: number;
	}>(),
	{
		numberOfSkeletonGroups: 2,
		numberOfSkeletonLinesPerGroup: 6
	}
);

const description = computed<string>(() => {
	if (!props.ad) return '';

	const text = props.ad.description.trim();

	return /[.!?]$/.test(text) ? text : `${text}.`;
});

const copyCommercialId = async () => {
	if (import.meta.client) await copy(props.commercialId ?? '');
	trackCopiedCommercialId({
		[MixpanelProps.COMMERCIAL_ID]: props.commercialId ?? ''
	});
};
</script>

<template>
	<div v-if="isLoading" class="flex w-full flex-col items-start gap-8">
		<div
			v-for="group in numberOfSkeletonGroups"
			:key="`property-description-section-skeleton-${group}`"
			class="flex w-full flex-col items-start gap-3"
		>
			<Skeleton
				v-for="line in numberOfSkeletonLinesPerGroup"
				:key="`property-description-section-skeleton-${group}-${line}`"
				:is-active="true"
				class="h-4 w-full last:w-3/5"
				variant="white"
			/>
		</div>
	</div>

	<OverflowContainer v-else>
		<span
			v-if="props.commercialId"
			class="flex items-center gap-1 pb-2 font-sans text-sm font-normal leading-[150%] text-primary-light-1"
		>
			ID {{ props.commercialId }}
			<CopyButton icon-class="ml-0" @click-copy="copyCommercialId" />
		</span>
		<h1
			v-if="props.ad?.title"
			class="font-sans text-[18px] font-medium leading-[150%] md:text-xl"
		>
			{{ props.ad?.title }}
		</h1>
		<p class="pt-3 first-letter:uppercase md-large:pt-5">
			{{ description }}
		</p>
	</OverflowContainer>
</template>
